import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import config from '../config/config';

function useApiStatus() {
  const [online, setOnline] = useState(false);
  useEffect(() => {
    const check = () =>
      axios.get(`${config.BASE_URL}/keepalive`)
        .then(() => setOnline(true))
        .catch(() => setOnline(false));
    check();
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, []);
  return online;
}

export default function Topbar() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const apiOnline = useApiStatus();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  return (
    <header className="ce-topbar">
      {/* Left — subtle page context / branding spacer */}
      <div className="ce-topbar-left">
        <span className="ce-topbar-appname">DepGuard</span>
        <span className="ce-topbar-version">v2</span>
      </div>

      {/* Right — utility controls */}
      <div className="ce-topbar-right">

        {/* API status */}
        <div className="ce-topbar-api" title={`API ${apiOnline ? 'online' : 'offline'}`}>
          <span className={`ce-api-dot${apiOnline ? ' online' : ''}`} />
          <span className="ce-topbar-api-label">
            API {apiOnline ? t('topbar.online', 'Online') : t('topbar.offline', 'Offline')}
          </span>
        </div>

        <div className="ce-topbar-divider" />

        {/* Language */}
        <div className="ce-topbar-langs">
          <button
            className={`ce-topbar-lang${i18n.language?.startsWith('en') ? ' active' : ''}`}
            onClick={() => i18n.changeLanguage('en')}
          >EN</button>
          <button
            className={`ce-topbar-lang${i18n.language?.startsWith('nl') ? ' active' : ''}`}
            onClick={() => i18n.changeLanguage('nl')}
          >NL</button>
        </div>

        {/* Theme */}
        <button
          className="ce-topbar-icon-btn"
          onClick={() => setDarkMode(p => !p)}
          title={darkMode ? t('topbar.lightMode', 'Switch to light mode') : t('topbar.darkMode', 'Switch to dark mode')}
        >
          {darkMode ? '☀️' : '🌙'}
        </button>

        <div className="ce-topbar-divider" />

        {/* User */}
        {user && (
          <>
            <div className="ce-topbar-user">
              <div className="ce-topbar-avatar" title={user.email}>
                {user.email.charAt(0).toUpperCase()}
              </div>
              <div className="ce-topbar-user-info">
                <span className="ce-topbar-user-email">{user.email}</span>
                <span className="ce-topbar-user-role">
                  <span className={`ce-role-dot ce-role-${user.role}`} />
                  {user.role}
                </span>
              </div>
            </div>

            {/* Logout */}
            <button
              className="ce-topbar-icon-btn ce-topbar-logout"
              onClick={() => { logout(); navigate('/login'); }}
              title={t('topbar.signOut', 'Sign out')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
              </svg>
            </button>
          </>
        )}
      </div>
    </header>
  );
}
